import React, { useState } from "react";

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /**
   * Label text for the input
   */
  label?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Show validation state
   */
  isValid?: boolean;
  /**
   * Helper text below input
   */
  helperText?: string;
  /**
   * Input size
   */
  size?: "sm" | "md" | "lg";
  /**
   * Variant style
   */
  variant?: "outline" | "filled";
  /**
   * Icon on the left
   */
  startIcon?: React.ReactNode;
  /**
   * Icon on the right
   */
  endIcon?: React.ReactNode;
  /**
   * Show character count
   */
  showCounter?: boolean;
  /**
   * Maximum character count
   */
  maxLength?: number;
  /**
   * Full width
   */
  fullWidth?: boolean;
}

export const TextField = ({
  label,
  error,
  isValid,
  helperText,
  size = "md",
  variant = "outline",
  startIcon,
  endIcon,
  showCounter,
  maxLength,
  fullWidth = false,
  className = "",
  required,
  ...props
}: TextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [characterCount, setCharacterCount] = useState(
    props.value?.toString().length || 0
  );

  const hasError = !!error;
  const showValidation = isValid !== undefined && !hasError;

  const baseStyles = "transition-colors";
  const sizeStyles = {
    sm: "px-3 py-2.5 text-sm",
    md: "px-4 py-3 text-sm",
    lg: "px-5 py-4 text-lg",
  };

  const variantStyles = {
    outline: hasError
      ? "border-transparent outline outline-2 outline-ds-danger bg-white dark:bg-gray-700"
      : showValidation
      ? "border-ds-success bg-white dark:bg-gray-700"
      : isFocused
      ? "border-ds-accent outline outline-2 outline-ds-accent outline-offset-0 bg-white dark:bg-gray-700"
      : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700",
    filled: hasError
      ? "bg-red-50 border-transparent outline outline-2 outline-ds-danger"
      : isFocused
      ? "bg-ds-card border border-ds-accent"
      : "bg-ds-card border border-ds-border",
  };

  const textColor =
    variant === "filled" ? "text-white" : "text-gray-900 dark:text-white";
  const placeholderColor =
    variant === "filled"
      ? "placeholder-ds-text"
      : "placeholder-gray-500 dark:placeholder-gray-400";

  const inputClassName = `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} rounded-lg w-full ${textColor} ${placeholderColor} focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (maxLength) {
      setCharacterCount(e.target.value.length);
    }
    props.onChange?.(e);
  };

  const labelClassName = `block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 ${
    required ? "after:content-['*'] after:ml-0.5 after:text-ds-danger" : ""
  }`;

  return (
    <div className={fullWidth ? "w-full" : ""}>
      {label && (
        <label htmlFor={props.id} className={labelClassName}>
          {label}
        </label>
      )}

      <div className="relative">
        {startIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-white/70">
            {startIcon}
          </div>
        )}

        <input
          {...props}
          onChange={handleChange}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          maxLength={maxLength}
          className={`${inputClassName} ${startIcon ? "pl-10" : ""} ${
            endIcon ? "pr-10" : ""
          } ${className}`}
        />

        {endIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-white/70">
            {endIcon}
          </div>
        )}
      </div>

      {(error || helperText || showCounter) && (
        <div className="mt-1 flex items-center justify-between">
          <div>
            {error && (
              <span className="text-xs leading-[18px] text-ds-danger">
                {error}
              </span>
            )}
            {!error && helperText && (
              <span className="text-xs leading-[18px] text-white/70">
                {helperText}
              </span>
            )}
          </div>
          {showCounter && maxLength && (
            <span
              className={`text-xs ${
                characterCount > maxLength * 0.9
                  ? "text-ds-danger"
                  : "text-white/70"
              }`}
            >
              {characterCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
